/**
 * TanStack Query Hooks for Workspace Item Operations
 *
 * Part of the Data Workspace OS feature set.
 * Provides mutations for managing tables and linked tables:
 * - Toggle favorite
 * - Move to folder
 * - Move to trash (soft delete)
 * - Restore from trash
 * - Permanent delete
 *
 * @module features/organization/hooks/useItemMutations
 */

import { useMutation, useQueryClient, notifyManager } from '@tanstack/react-query';
import type { WorkspaceItemType } from '@loader/shared-types';

import { useRpc, extractErrorMessage } from '@/lib/rpc';
import { queryKeys } from '@/lib/queryKeys';

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Invalidate all workspace-related queries after mutations
 * This ensures consistent cache synchronization across all workspace views
 */
function invalidateWorkspaceQueries(queryClient: ReturnType<typeof useQueryClient>): void {
  void queryClient.invalidateQueries({ queryKey: queryKeys.tables.all });
  void queryClient.invalidateQueries({ queryKey: queryKeys.linkedTables.all });
  void queryClient.invalidateQueries({ queryKey: queryKeys.organization.folders.all });
  void queryClient.invalidateQueries({ queryKey: queryKeys.organization.folders.trash });
  void queryClient.invalidateQueries({ queryKey: queryKeys.organization.items.all });
  void queryClient.invalidateQueries({ queryKey: queryKeys.connectors.all });
}

// ============================================================================
// TYPES
// ============================================================================

type ItemType = 'table' | 'linked_table' | 'folder';

interface ItemIdentifier {
  itemType: WorkspaceItemType;
  itemId: string;
}

/** Shape of the trash cache data for optimistic updates */
interface TrashCacheData {
  folders: Array<{ id: string }>;
  tables: Array<{ id: string }>;
  linked_tables: Array<{ id: string }>;
}

// Convert WorkspaceItemType to API item_type
function toApiItemType(type: WorkspaceItemType): ItemType {
  if (type === 'linked_table') return 'linked_table';
  if (type === 'folder') return 'folder';
  return 'table';
}

// ============================================================================
// MUTATION HOOKS
// ============================================================================

/**
 * Toggle favorite status for a table or linked table
 */
export function useToggleFavoriteMutation() {
  const rpc = useRpc();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ itemType, itemId }: ItemIdentifier) => {
      // @ts-expect-error - Hono RPC type inference limitation
      const res = await rpc.api.private.organization.items.favorite.$post({
        json: {
          item_type: toApiItemType(itemType),
          item_id: itemId,
        },
      });

      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, 'Failed to toggle favorite'));
      }

      return res.json();
    },
    onSuccess: (_data, variables) => {
      // Invalidate relevant queries
      if (variables.itemType === 'table') {
        void queryClient.invalidateQueries({ queryKey: queryKeys.tables.all });
        void queryClient.invalidateQueries({ queryKey: queryKeys.tables.detail(variables.itemId) });
      } else {
        void queryClient.invalidateQueries({ queryKey: queryKeys.linkedTables.all });
        void queryClient.invalidateQueries({ queryKey: queryKeys.linkedTables.detail(variables.itemId) });
      }
      void queryClient.invalidateQueries({ queryKey: queryKeys.organization.items.all });
    },
  });
}

/**
 * Move a single item to a folder
 *
 * Uses optimistic updates for instant UI feedback.
 */
export function useMoveToFolderMutation() {
  const rpc = useRpc();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ itemType, itemId, folderId }: ItemIdentifier & { folderId: string | null }) => {
      // @ts-expect-error - Hono RPC type inference limitation
      const res = await rpc.api.private.organization.items.move.$post({
        json: {
          item_type: toApiItemType(itemType),
          item_id: itemId,
          folder_id: folderId,
        },
      });

      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, 'Failed to move item'));
      }

      return res.json();
    },
    onMutate: async (variables) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.tables.all });
      await queryClient.cancelQueries({ queryKey: queryKeys.linkedTables.all });
      await queryClient.cancelQueries({ queryKey: queryKeys.organization.folders.all });

      // Snapshot previous values for rollback
      const previousTables = queryClient.getQueryData(queryKeys.tables.list());
      const previousLinkedTables = queryClient.getQueryData(queryKeys.linkedTables.list());
      const previousFolders = queryClient.getQueryData(queryKeys.organization.folders.list());

      // Optimistically update the cache
      if (variables.itemType === 'table') {
        queryClient.setQueryData(
          queryKeys.tables.list(),
          (old: Array<{ id: string; folder_id?: string | null }> | undefined) => {
            if (!old) return old;
            return old.map((item) =>
              item.id === variables.itemId
                ? { ...item, folder_id: variables.folderId }
                : item
            );
          }
        );
      } else if (variables.itemType === 'linked_table') {
        queryClient.setQueryData(
          queryKeys.linkedTables.list(),
          (old: Array<{ id: string; folder_id?: string | null }> | undefined) => {
            if (!old) return old;
            return old.map((item) =>
              item.id === variables.itemId
                ? { ...item, folder_id: variables.folderId }
                : item
            );
          }
        );
      } else if (variables.itemType === 'folder') {
        // Folders use parent_folder_id instead of folder_id
        queryClient.setQueryData(
          queryKeys.organization.folders.list(),
          (old: Array<{ id: string; parent_folder_id?: string | null }> | undefined) => {
            if (!old) return old;
            return old.map((item) =>
              item.id === variables.itemId
                ? { ...item, parent_folder_id: variables.folderId }
                : item
            );
          }
        );
      }

      return { previousTables, previousLinkedTables, previousFolders };
    },
    onError: (_err, variables, context) => {
      // Roll back to the previous value on error
      if (variables.itemType === 'table' && context?.previousTables) {
        queryClient.setQueryData(queryKeys.tables.list(), context.previousTables);
      } else if (variables.itemType === 'linked_table' && context?.previousLinkedTables) {
        queryClient.setQueryData(queryKeys.linkedTables.list(), context.previousLinkedTables);
      } else if (variables.itemType === 'folder' && context?.previousFolders) {
        queryClient.setQueryData(queryKeys.organization.folders.list(), context.previousFolders);
      }
    },
    onSettled: (_data, _error, variables) => {
      // Always refetch after error or success to ensure cache is in sync
      if (variables.itemType === 'table') {
        void queryClient.invalidateQueries({ queryKey: queryKeys.tables.all });
      } else if (variables.itemType === 'linked_table') {
        void queryClient.invalidateQueries({ queryKey: queryKeys.linkedTables.all });
      }
      // Always invalidate folders (could be moving into a folder or moving a folder)
      void queryClient.invalidateQueries({ queryKey: queryKeys.organization.folders.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.organization.items.all });
    },
  });
}

/**
 * Move an item to trash (soft delete)
 *
 * Uses optimistic updates for instant UI feedback.
 */
export function useTrashItemMutation() {
  const rpc = useRpc();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ itemType, itemId }: ItemIdentifier) => {
      // @ts-expect-error - Hono RPC type inference limitation
      const res = await rpc.api.private.organization.items.trash.$post({
        json: {
          item_type: toApiItemType(itemType),
          item_id: itemId,
        },
      });

      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, 'Failed to move item to trash'));
      }

      return res.json();
    },
    onMutate: async (variables) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.tables.all });
      await queryClient.cancelQueries({ queryKey: queryKeys.linkedTables.all });
      await queryClient.cancelQueries({ queryKey: queryKeys.organization.folders.all });

      // Snapshot previous values for rollback
      const previousTables = queryClient.getQueryData(queryKeys.tables.list());
      const previousLinkedTables = queryClient.getQueryData(queryKeys.linkedTables.list());
      const previousFolders = queryClient.getQueryData(queryKeys.organization.folders.list());

      // Optimistically remove item from the list by setting deleted_at
      if (variables.itemType === 'table') {
        queryClient.setQueryData(
          queryKeys.tables.list(),
          (old: Array<{ id: string; deleted_at?: string | null }> | undefined) => {
            if (!old) return old;
            return old.filter((item) => item.id !== variables.itemId);
          }
        );
      } else if (variables.itemType === 'linked_table') {
        queryClient.setQueryData(
          queryKeys.linkedTables.list(),
          (old: Array<{ id: string; deleted_at?: string | null }> | undefined) => {
            if (!old) return old;
            return old.filter((item) => item.id !== variables.itemId);
          }
        );
      } else if (variables.itemType === 'folder') {
        queryClient.setQueryData(
          queryKeys.organization.folders.list(),
          (old: Array<{ id: string; deleted_at?: string | null }> | undefined) => {
            if (!old) return old;
            return old.filter((item) => item.id !== variables.itemId);
          }
        );
      }

      return { previousTables, previousLinkedTables, previousFolders };
    },
    onError: (_err, variables, context) => {
      // Roll back to the previous value on error
      if (variables.itemType === 'table' && context?.previousTables) {
        queryClient.setQueryData(queryKeys.tables.list(), context.previousTables);
      } else if (variables.itemType === 'linked_table' && context?.previousLinkedTables) {
        queryClient.setQueryData(queryKeys.linkedTables.list(), context.previousLinkedTables);
      } else if (variables.itemType === 'folder' && context?.previousFolders) {
        queryClient.setQueryData(queryKeys.organization.folders.list(), context.previousFolders);
      }
    },
    onSettled: (_data, _error, variables) => {
      // Always refetch after error or success to ensure cache is in sync
      if (variables.itemType === 'table') {
        void queryClient.invalidateQueries({ queryKey: queryKeys.tables.all });
        void queryClient.invalidateQueries({ queryKey: queryKeys.connectors.all });
      } else if (variables.itemType === 'linked_table') {
        void queryClient.invalidateQueries({ queryKey: queryKeys.linkedTables.all });
      }
      void queryClient.invalidateQueries({ queryKey: queryKeys.organization.folders.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.organization.folders.trash });
      void queryClient.invalidateQueries({ queryKey: queryKeys.organization.items.all });
    },
  });
}

/**
 * Restore an item from trash
 *
 * Uses optimistic updates for instant UI feedback.
 */
export function useRestoreItemMutation() {
  const rpc = useRpc();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ itemType, itemId }: ItemIdentifier) => {
      // @ts-expect-error - Hono RPC type inference limitation
      const res = await rpc.api.private.organization.items.restore.$post({
        json: {
          item_type: toApiItemType(itemType),
          item_id: itemId,
        },
      });

      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, 'Failed to restore item'));
      }

      return res.json();
    },
    onMutate: async (variables) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.organization.folders.trash });

      // Snapshot trash items for rollback
      const previousTrash = queryClient.getQueryData(queryKeys.organization.folders.trash);

      // Optimistically remove from trash view
      queryClient.setQueryData(
        queryKeys.organization.folders.trash,
        (old: TrashCacheData | undefined) => {
          if (!old) return old;
          if (variables.itemType === 'table') {
            return {
              ...old,
              tables: old.tables.filter((t) => t.id !== variables.itemId),
            };
          } else if (variables.itemType === 'linked_table') {
            return {
              ...old,
              linked_tables: old.linked_tables.filter((lt) => lt.id !== variables.itemId),
            };
          } else if (variables.itemType === 'folder') {
            return {
              ...old,
              folders: old.folders.filter((f) => f.id !== variables.itemId),
            };
          }
          return old;
        }
      );

      return { previousTrash };
    },
    onError: (_err, _variables, context) => {
      // Roll back to the previous value on error
      if (context?.previousTrash) {
        queryClient.setQueryData(queryKeys.organization.folders.trash, context.previousTrash);
      }
    },
    onSettled: (_data, _error, variables) => {
      // Always refetch after error or success to ensure cache is in sync
      if (variables.itemType === 'table') {
        void queryClient.invalidateQueries({ queryKey: queryKeys.tables.all });
        void queryClient.invalidateQueries({ queryKey: queryKeys.connectors.all });
      } else if (variables.itemType === 'linked_table') {
        void queryClient.invalidateQueries({ queryKey: queryKeys.linkedTables.all });
      }
      void queryClient.invalidateQueries({ queryKey: queryKeys.organization.folders.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.organization.folders.trash });
      void queryClient.invalidateQueries({ queryKey: queryKeys.organization.items.all });
    },
  });
}

/**
 * Bulk restore items from trash
 *
 * Uses optimistic updates with batching for instant UI feedback.
 * All items visually disappear at once.
 */
export function useBulkRestoreMutation() {
  const rpc = useRpc();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (items: ItemIdentifier[]) => {
      // Restore items in parallel (no bulk API endpoint, but can run concurrently)
      const results = await Promise.all(
        items.map(async (item) => {
          // @ts-expect-error - Hono RPC type inference limitation
          const res = await rpc.api.private.organization.items.restore.$post({
            json: {
              item_type: toApiItemType(item.itemType),
              item_id: item.itemId,
            },
          });

          if (!res.ok) {
            throw new Error(await extractErrorMessage(res, 'Failed to restore items'));
          }

          return res.json();
        })
      );
      return results;
    },
    onMutate: async (items) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.organization.folders.trash });

      // Snapshot trash items for rollback
      const previousTrash = queryClient.getQueryData(queryKeys.organization.folders.trash);

      // Collect all IDs to remove by type
      const tableIds = new Set(items.filter(i => i.itemType === 'table').map(i => i.itemId));
      const linkedTableIds = new Set(items.filter(i => i.itemType === 'linked_table').map(i => i.itemId));
      const folderIds = new Set(items.filter(i => i.itemType === 'folder').map(i => i.itemId));

      // Batch all cache updates to trigger a single React re-render
      notifyManager.batch(() => {
        queryClient.setQueryData(
          queryKeys.organization.folders.trash,
          (old: TrashCacheData | undefined) => {
            if (!old) return old;
            return {
              ...old,
              tables: old.tables.filter((t) => !tableIds.has(t.id)),
              linked_tables: old.linked_tables.filter((lt) => !linkedTableIds.has(lt.id)),
              folders: old.folders.filter((f) => !folderIds.has(f.id)),
            };
          }
        );
      });

      return { previousTrash };
    },
    onError: (_err, _variables, context) => {
      // Roll back to the previous value on error
      if (context?.previousTrash) {
        queryClient.setQueryData(queryKeys.organization.folders.trash, context.previousTrash);
      }
    },
    onSettled: () => {
      // Always refetch after error or success to ensure cache is in sync
      invalidateWorkspaceQueries(queryClient);
    },
  });
}

/**
 * Duplicate a linked table
 */
export function useDuplicateLinkedTableMutation() {
  const rpc = useRpc();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (linkedTableId: string) => {
      // @ts-expect-error - Hono RPC type inference limitation
      const res = await rpc.api.private['linked-tables'][':id'].duplicate.$post({
        param: { id: linkedTableId },
      });

      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, 'Failed to duplicate linked table'));
      }

      return res.json();
    },
    onSuccess: () => {
      invalidateWorkspaceQueries(queryClient);
    },
  });
}

/** Response type for 409 conflict when table has dependencies */
export interface DependencyConflictError {
  error: string;
  dependent_linked_tables: Array<{ id: string; name: string }>;
  message: string;
  request_id: string;
}

/** Custom error class for dependency conflicts */
export class DependencyError extends Error {
  public readonly dependentLinkedTables: Array<{ id: string; name: string }>;

  constructor(message: string, dependentLinkedTables: Array<{ id: string; name: string }>) {
    super(message);
    this.name = 'DependencyError';
    this.dependentLinkedTables = dependentLinkedTables;
  }
}

/**
 * Permanently delete an item (must be in trash first)
 *
 * For tables with dependent linked tables:
 * - Without cascade: throws DependencyError with list of dependencies
 * - With cascade: true: deletes the table and all dependent linked tables
 *
 * Uses optimistic updates for instant UI feedback.
 */
export function usePermanentDeleteMutation() {
  const rpc = useRpc();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ itemType, itemId, cascade }: ItemIdentifier & { cascade?: boolean }) => {
      // @ts-expect-error - Hono RPC type inference limitation
      const res = await rpc.api.private.organization.items['permanent-delete'].$post({
        json: {
          item_type: toApiItemType(itemType),
          item_id: itemId,
          cascade,
        },
      });

      if (!res.ok) {
        // Check for 409 conflict (dependency error)
        if (res.status === 409) {
          const data = await res.json() as DependencyConflictError;
          throw new DependencyError(
            data.message || 'Table has dependent linked tables',
            data.dependent_linked_tables || []
          );
        }
        throw new Error(await extractErrorMessage(res, 'Failed to permanently delete item'));
      }

      return res.json();
    },
    onMutate: async (variables) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.organization.folders.trash });

      // Snapshot trash items for rollback
      const previousTrash = queryClient.getQueryData(queryKeys.organization.folders.trash);

      // Optimistically remove from trash view
      queryClient.setQueryData(
        queryKeys.organization.folders.trash,
        (old: TrashCacheData | undefined) => {
          if (!old) return old;
          if (variables.itemType === 'table') {
            return {
              ...old,
              tables: old.tables.filter((t) => t.id !== variables.itemId),
            };
          } else if (variables.itemType === 'linked_table') {
            return {
              ...old,
              linked_tables: old.linked_tables.filter((lt) => lt.id !== variables.itemId),
            };
          } else if (variables.itemType === 'folder') {
            return {
              ...old,
              folders: old.folders.filter((f) => f.id !== variables.itemId),
            };
          }
          return old;
        }
      );

      return { previousTrash };
    },
    onError: (_err, _variables, context) => {
      // Roll back to the previous value on error (except for DependencyError which is expected)
      if (context?.previousTrash && !(_err instanceof DependencyError)) {
        queryClient.setQueryData(queryKeys.organization.folders.trash, context.previousTrash);
      }
    },
    onSettled: (_data, _error, variables) => {
      // Always refetch after error or success to ensure cache is in sync
      if (variables.itemType === 'table') {
        void queryClient.invalidateQueries({ queryKey: queryKeys.tables.all });
        void queryClient.invalidateQueries({ queryKey: queryKeys.connectors.all });
      }
      // Always invalidate linked tables (might have been cascade deleted)
      void queryClient.invalidateQueries({ queryKey: queryKeys.linkedTables.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.organization.folders.trash });
      void queryClient.invalidateQueries({ queryKey: queryKeys.organization.items.all });
    },
  });
}

/**
 * Bulk move items to a folder
 */
export function useBulkMoveToFolderMutation() {
  const rpc = useRpc();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ items, folderId }: { items: ItemIdentifier[]; folderId: string | null }) => {
      // @ts-expect-error - Hono RPC type inference limitation
      const res = await rpc.api.private.organization.items['bulk-move'].$post({
        json: {
          items: items.map(item => ({
            item_type: toApiItemType(item.itemType),
            item_id: item.itemId,
          })),
          folder_id: folderId,
        },
      });

      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, 'Failed to move items'));
      }

      return res.json();
    },
    onSuccess: () => {
      // Invalidate all relevant queries
      void queryClient.invalidateQueries({ queryKey: queryKeys.tables.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.linkedTables.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.organization.folders.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.organization.items.all });
    },
  });
}

/**
 * Bulk move items to trash
 *
 * Uses optimistic updates for instant UI feedback.
 */
export function useBulkTrashMutation() {
  const rpc = useRpc();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (items: ItemIdentifier[]) => {
      // @ts-expect-error - Hono RPC type inference limitation
      const res = await rpc.api.private.organization.items['bulk-trash'].$post({
        json: {
          items: items.map(item => ({
            item_type: toApiItemType(item.itemType),
            item_id: item.itemId,
          })),
        },
      });

      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, 'Failed to trash items'));
      }

      return res.json();
    },
    onMutate: async (items) => {
      // Cancel any outgoing refetches in parallel
      await Promise.all([
        queryClient.cancelQueries({ queryKey: queryKeys.tables.all }),
        queryClient.cancelQueries({ queryKey: queryKeys.linkedTables.all }),
        queryClient.cancelQueries({ queryKey: queryKeys.organization.folders.all }),
      ]);

      // Snapshot previous values for rollback
      const previousTables = queryClient.getQueryData(queryKeys.tables.list());
      const previousLinkedTables = queryClient.getQueryData(queryKeys.linkedTables.list());
      const previousFolders = queryClient.getQueryData(queryKeys.organization.folders.list());

      // Collect all IDs to remove
      const allIds = new Set(items.map(i => i.itemId));

      // Batch all cache updates to trigger a single React re-render
      notifyManager.batch(() => {
        queryClient.setQueryData(
          queryKeys.tables.list(),
          (old: Array<{ id: string }> | undefined) => {
            if (!old) return old;
            return old.filter((item) => !allIds.has(item.id));
          }
        );
        queryClient.setQueryData(
          queryKeys.linkedTables.list(),
          (old: Array<{ id: string }> | undefined) => {
            if (!old) return old;
            return old.filter((item) => !allIds.has(item.id));
          }
        );
        queryClient.setQueryData(
          queryKeys.organization.folders.list(),
          (old: Array<{ id: string }> | undefined) => {
            if (!old) return old;
            return old.filter((item) => !allIds.has(item.id));
          }
        );
      });

      return { previousTables, previousLinkedTables, previousFolders };
    },
    onError: (_err, _variables, context) => {
      // Roll back to the previous values on error - batch to prevent multiple re-renders
      notifyManager.batch(() => {
        if (context?.previousTables) {
          queryClient.setQueryData(queryKeys.tables.list(), context.previousTables);
        }
        if (context?.previousLinkedTables) {
          queryClient.setQueryData(queryKeys.linkedTables.list(), context.previousLinkedTables);
        }
        if (context?.previousFolders) {
          queryClient.setQueryData(queryKeys.organization.folders.list(), context.previousFolders);
        }
      });
    },
    onSettled: () => {
      // Always refetch after error or success to ensure cache is in sync
      invalidateWorkspaceQueries(queryClient);
    },
  });
}

/**
 * Empty trash - permanently delete all items in trash
 *
 * Uses optimistic updates for instant UI feedback.
 */
export function useEmptyTrashMutation() {
  const rpc = useRpc();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      // @ts-expect-error - Hono RPC type inference limitation
      const res = await rpc.api.private.organization.items['empty-trash'].$post();

      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, 'Failed to empty trash'));
      }

      return res.json();
    },
    onMutate: async () => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.organization.folders.trash });

      // Snapshot trash items for rollback
      const previousTrash = queryClient.getQueryData(queryKeys.organization.folders.trash);

      // Optimistically clear trash
      queryClient.setQueryData(
        queryKeys.organization.folders.trash,
        (old: TrashCacheData | undefined) => {
          if (!old) return old;
          return {
            ...old,
            folders: [],
            tables: [],
            linked_tables: [],
          };
        }
      );

      return { previousTrash };
    },
    onError: (_err, _variables, context) => {
      // Roll back to the previous value on error
      if (context?.previousTrash) {
        queryClient.setQueryData(queryKeys.organization.folders.trash, context.previousTrash);
      }
    },
    onSettled: () => {
      // Always refetch after error or success to ensure cache is in sync
      invalidateWorkspaceQueries(queryClient);
    },
  });
}
